import React, { useMemo, useState } from 'react';
import type { IconKind } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { GROWTH_WEBSITE_HEALTH_META } from '../../growth-website-health/config';
import {
  WH_ISSUES,
  WH_SEVERITY,
  computeHealthScore,
  computeIssueCounts,
} from '../../growth-website-health/mockData';
import type {
  WhFilterKey,
  WhIssue,
  WhReportSummary,
  WhToneKey,
} from '../../growth-website-health/types';
import './growth-website-health.css';

const WH_TONE: Record<WhToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
};

const whEyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

function WhRing({ score, size = 110 }: { score: number; size?: number }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const toneKey: WhToneKey = score >= 80 ? 'success' : score >= 50 ? 'gold' : 'terra';
  const tone = WH_TONE[toneKey];

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-mute)"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone.accent}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ - (score / 100) * circ}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span
          className="font-[var(--font-display)] font-extrabold tracking-[-0.04em] text-[var(--ink-0)]"
          style={{ fontSize: size * 0.31 }}
        >
          {score}
        </span>
        <span className="mt-0.5 text-[10px] text-[var(--ink-4)]">/ 100</span>
      </div>
    </div>
  );
}

function WhDetailCol({
  label,
  tone,
  icon,
  text,
  border,
  bg,
}: {
  label: string;
  tone: WhToneKey;
  icon: IconKind;
  text: string;
  border?: boolean;
  bg?: string;
}) {
  const t = WH_TONE[tone];
  return (
    <div
      className="px-[22px] py-5"
      style={{
        borderRight: border ? '1px solid var(--cream-2)' : undefined,
        background: bg ?? 'transparent',
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px]"
          style={{ background: t.tint, color: t.accent }}
        >
          <Icon kind={icon} size={14} stroke={1.9} />
        </span>
        <span
          className="text-[11px] font-bold uppercase tracking-[0.06em]"
          style={{ color: t.accent }}
        >
          {label}
        </span>
      </div>
      <div className="text-[13px] leading-[1.65] text-[var(--ink-1)]">{text}</div>
    </div>
  );
}

function IssueCard({
  issue,
  open,
  onToggle,
}: {
  issue: WhIssue;
  open: boolean;
  onToggle: () => void;
}) {
  const meta = GROWTH_WEBSITE_HEALTH_META;
  const severity = WH_SEVERITY[issue.severity] ?? WH_SEVERITY.minor;
  const tone = WH_TONE[severity.tone] ?? WH_TONE.info;
  const isGood = issue.severity === 'good';

  return (
    <div
      className="sa-issue overflow-hidden rounded-[var(--r-xl)] bg-[var(--cream-1)]"
      style={{
        border: open
          ? `1px solid color-mix(in srgb, ${tone.accent} 45%, transparent)`
          : '1px solid var(--cream-2)',
        boxShadow: open ? 'var(--sh-2)' : 'var(--sh-1)',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="sa-head flex cursor-pointer items-center gap-[15px] px-[22px] py-[17px] transition-[background] duration-[var(--dur-fast)]"
      >
        <span
          className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
          style={{ background: tone.tint, color: tone.accent }}
        >
          <Icon kind={severity.icon} size={17} stroke={1.9} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-[3px] flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-base font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              {issue.title}
            </span>
            <span
              className="rounded-[var(--r-pill)] px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-[0.04em]"
              style={{ background: tone.tint, color: tone.accent }}
            >
              {severity.label}
            </span>
          </div>
          <div className="pr-3 text-[12.5px] leading-[1.5] text-[var(--ink-3)]">
            {issue.oneLiner}
          </div>
        </div>
        {(issue.patientsLost || isGood) && (
          <div
            className="shrink-0 rounded-[var(--r-md)] px-3.5 py-[7px] text-center"
            style={{ background: isGood ? 'var(--success-tint)' : 'var(--terracotta-tint)' }}
          >
            <div
              className="font-[var(--font-display)] text-lg font-bold tracking-[-0.02em]"
              style={{ color: isGood ? 'var(--success)' : 'var(--terracotta)' }}
            >
              {isGood ? '✓' : `~${issue.patientsLost}`}
            </div>
            <div
              className="text-[9px] font-bold uppercase tracking-[0.03em]"
              style={{ color: isGood ? 'var(--success)' : 'var(--terracotta)' }}
            >
              {isGood ? 'No issues' : 'pts/mo lost'}
            </div>
          </div>
        )}
        <Icon
          kind="down"
          size={17}
          stroke={2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </div>

      {open && !isGood && issue.problem && issue.effect && issue.solution ? (
        <div className="elva-fade border-t border-[var(--cream-2)]">
          <div className="wh-detail-grid grid grid-cols-3">
            <WhDetailCol label="The problem" tone="terra" icon="x" text={issue.problem} border />
            <WhDetailCol
              label="The effect"
              tone="gold"
              icon="alert"
              text={issue.effect}
              border
              bg="var(--cream-0)"
            />
            <WhDetailCol label="The fix" tone="success" icon="check-circ" text={issue.solution} />
          </div>
          {issue.pages ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-[var(--cream-2)] px-[22px] py-[13px]">
              <span className={whEyebrowClass}>Pages affected</span>
              <div className="flex flex-wrap gap-1.5">
                {issue.pages.map((page) => (
                  <span
                    key={page}
                    className="rounded-[5px] bg-[var(--surface-mute)] px-[9px] py-[3px] font-[var(--font-mono)] text-[11px] font-medium text-[var(--ink-2)]"
                  >
                    {page}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {open && isGood ? (
        <div className="elva-fade flex items-center gap-2.5 border-t border-[var(--cream-2)] px-[22px] py-4 text-[13px] leading-[1.6] text-[var(--ink-2)]">
          <Icon
            kind="check-circ"
            size={16}
            stroke={1.9}
            className="shrink-0 text-[var(--success)]"
          />
          {meta.goodPassingNote}
        </div>
      ) : null}
    </div>
  );
}

export interface WebsiteHealthReportViewProps {
  items?: WhIssue[];
  summary?: WhReportSummary;
  loading?: boolean;
  onShare?: () => void;
  onExport?: () => void;
}

export function WebsiteHealthReportView({
  items: itemsProp,
  summary: summaryProp,
  loading = false,
  onShare,
  onExport,
}: WebsiteHealthReportViewProps = {}) {
  const meta = GROWTH_WEBSITE_HEALTH_META;
  const controlled = itemsProp !== undefined;
  const sourceIssues = controlled ? itemsProp : WH_ISSUES;
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set([1]));
  const [filter, setFilter] = useState<WhFilterKey>('all');

  const computedCounts = useMemo(() => computeIssueCounts(sourceIssues), [sourceIssues]);
  const computedScore = useMemo(() => computeHealthScore(computedCounts), [computedCounts]);

  const counts = useMemo(() => {
    if (controlled && summaryProp) {
      return {
        ...summaryProp.counts,
        lost: summaryProp.patientsLost,
      };
    }
    return computedCounts;
  }, [computedCounts, controlled, summaryProp]);

  const score = controlled && summaryProp ? Math.round(summaryProp.score) : computedScore;
  const revenueDisplay =
    controlled && summaryProp && summaryProp.revenueLostCents > 0
      ? `$${(summaryProp.revenueLostCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : `$${(counts.lost * meta.revenuePerPatient).toLocaleString()}`;
  const scanNote = controlled && summaryProp ? summaryProp.scanNote : meta.footerScanNote;

  const filtered = useMemo(
    () =>
      filter === 'all' ? sourceIssues : sourceIssues.filter((issue) => issue.severity === filter),
    [filter, sourceIssues]
  );

  const filterOptions: { id: WhFilterKey; label: string; count: number }[] = [
    { id: 'all', label: 'All issues', count: sourceIssues.length },
    { id: 'critical', label: 'Critical', count: counts.critical },
    { id: 'important', label: 'Important', count: counts.important },
    { id: 'minor', label: 'Minor', count: counts.minor },
    { id: 'good', label: 'Passing', count: counts.good },
  ];

  const toggle = (id: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="growth-website-health-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-[600px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="search" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" iconLeft="download" onClick={onExport}>
            Export PDF
          </Button>
        </div>
      </div>

      <div className="wh-score-panel mb-6 flex flex-wrap items-center gap-8 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[26px_30px] shadow-[var(--sh-1)]">
        <div className="flex flex-col items-center gap-2">
          <WhRing score={score} />
          <span className={whEyebrowClass}>{meta.scoreLabel}</span>
        </div>
        <div className="min-w-[280px] flex-1">
          <div className="mb-[7px] font-[var(--font-display)] text-[19px] font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
            {meta.scoreTitle}
          </div>
          <div className="mb-4 max-w-[480px] text-[13.5px] leading-[1.65] text-[var(--ink-2)]">
            ELVA found{' '}
            <b className="font-semibold text-[var(--terracotta)]">{counts.critical} critical</b> and{' '}
            <b className="font-semibold text-[var(--gold)]">{counts.important} important</b> issues
            — estimated to cost you about{' '}
            <b className="font-semibold text-[var(--terracotta)]">
              ~{counts.lost} potential patients every month
            </b>
            .
          </div>
          <div className="wh-severity-grid flex flex-wrap gap-2">
            {(
              [
                ['critical', counts.critical, 'Critical'],
                ['important', counts.important, 'Important'],
                ['minor', counts.minor, 'Minor'],
                ['good', counts.good, 'Passing'],
              ] as const
            ).map(([severity, count, label]) => {
              const sev = WH_SEVERITY[severity] ?? WH_SEVERITY.minor;
              const t = WH_TONE[sev.tone] ?? WH_TONE.info;
              return (
                <div
                  key={severity}
                  className="min-w-[64px] rounded-[var(--r-md)] border px-4 py-2 text-center"
                  style={{
                    background: t.tint,
                    borderColor: `color-mix(in srgb, ${t.accent} 24%, transparent)`,
                  }}
                >
                  <div
                    className="font-[var(--font-display)] text-xl font-extrabold tracking-[-0.03em]"
                    style={{ color: t.accent }}
                  >
                    {count}
                  </div>
                  <div
                    className="text-[10px] font-bold uppercase tracking-[0.04em]"
                    style={{ color: t.accent }}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="elva-grad-ink wh-lost-card relative max-w-[220px] shrink-0 overflow-hidden rounded-[var(--r-lg)] p-5 shadow-[var(--sh-2)]">
          <div className="elva-grad-spectrum-fill pointer-events-none absolute -right-[30px] -top-[50px] h-[140px] w-[140px] rounded-full opacity-40 blur-[48px]" />
          <div className="relative text-white">
            <div className="font-[var(--font-display)] text-[38px] font-extrabold leading-none tracking-[-0.04em]">
              ~{counts.lost}
            </div>
            <div className="mt-1.5 text-[12.5px] leading-[1.5] text-white/80">
              estimated patients lost per month due to website issues
            </div>
            <div className="mt-2 font-[var(--font-mono)] text-[11.5px] text-white/60">
              ≈ {revenueDisplay}/mo in revenue
            </div>
          </div>
        </div>
      </div>

      <div className="mb-5 inline-flex flex-wrap gap-[3px] rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1 shadow-[var(--sh-1)]">
        {filterOptions.map((option) => {
          const active = filter === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setFilter(option.id)}
              className={[
                'sa-ft inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[var(--r-pill)] border-0 px-3.5 py-1.5 font-[var(--font-body)] text-[12.5px]',
                active
                  ? 'sa-ft--on bg-[var(--surface-mute)] font-semibold text-[var(--ink-0)]'
                  : 'bg-transparent font-medium text-[var(--ink-3)]',
              ].join(' ')}
            >
              {option.label}
              <span className="rounded-[5px] bg-[var(--cream-0)] px-1.5 py-px font-[var(--font-mono)] text-[10px] font-bold text-[var(--ink-3)]">
                {option.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3">
        {filtered.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            open={expanded.has(issue.id)}
            onToggle={() => toggle(issue.id)}
          />
        ))}
      </div>

      <div className="elva-grad-ink relative mt-[26px] flex flex-wrap items-center justify-between gap-5 overflow-hidden rounded-[var(--r-xl)] p-[26px_30px] shadow-[var(--sh-2)]">
        <div className="elva-grad-spectrum-fill pointer-events-none absolute -right-20 -top-[70px] h-[220px] w-[220px] rounded-full opacity-[0.34] blur-[66px]" />
        <div className="relative max-w-[540px] text-white">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="sparkle" size={17} stroke={2} className="text-white" />
            <span className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em]">
              {meta.ctaTitle}
            </span>
          </div>
          <p className="m-0 text-[13.5px] leading-[1.6] text-white/80">{meta.ctaDescription}</p>
        </div>
        <div className="relative flex shrink-0 flex-wrap gap-2.5">
          <button
            type="button"
            className="cursor-pointer rounded-[var(--r-md)] border-0 bg-[var(--cream-1)] px-[22px] py-[11px] font-[var(--font-body)] text-[13.5px] font-semibold text-[var(--ink-0)]"
          >
            Contact ELVA team
          </button>
        </div>
      </div>

      <div className="mt-[22px] flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">{scanNote}</span>
        <span className="text-xs text-[var(--ink-4)]">{meta.footerRescanNote}</span>
      </div>
    </div>
  );
}
